import { useRefreshTick } from "../lib/useAutoRefresh";
import { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { money, FREQ_LABELS, fmtDateTime } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { History, ArrowUpRight, Trash2, RotateCcw, ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import CompanyBreadcrumb from "../components/CompanyBreadcrumb";

const ACCESSORS = {
  run_number: (r) => r.run_number || "",
  pay_date: (r) => r.pay_date || "",
  period: (r) => r.pay_period_start || "",
  frequency: (r) => FREQ_LABELS[r.pay_frequency] || "",
  emp: (r) => r.employee_count || 0,
  gross: (r) => r.totals?.gross || 0,
  ded: (r) => r.totals?.total_deductions || 0,
  net: (r) => r.totals?.net_pay || 0,
};

export default function PayrollHistory() {
  const base = useLocation().pathname.startsWith("/client") ? "/client" : "";
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [runs, setRuns] = useState([]);
  const [reopenEvents, setReopenEvents] = useState([]);
  const [sort, setSort] = useState({ key: "pay_date", dir: "desc" });

  const toggleSort = (key) =>
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }));

  const years = useMemo(() => [...new Set(runs.map((r) => String(r.pay_date || "").slice(0, 4)).filter(Boolean))].sort().reverse(), [runs]);
  const [year, setYear] = useState(String(new Date().getFullYear()));
  useEffect(() => { if (years.length && !years.includes(year)) setYear(years[0]); }, [years]); // eslint-disable-line react-hooks/exhaustive-deps
  const yearRuns = useMemo(() => runs.filter((r) => String(r.pay_date || "").slice(0, 4) === year), [runs, year]);
  const sorted = useMemo(() => {
    const acc = ACCESSORS[sort.key];
    return [...yearRuns].sort((a, b) => {
      const va = acc(a), vb = acc(b);
      let cmp;
      if (typeof va === "number" && typeof vb === "number") cmp = va - vb;
      else cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [yearRuns, sort]);

  const Th = ({ col, label, align = "left" }) => {
    const active = sort.key === col;
    const justify = align === "right" ? "justify-end" : align === "center" ? "justify-center" : "justify-start";
    return (
      <th className={`px-5 py-3 ${align === "right" ? "text-right" : align === "center" ? "text-center" : "text-left"}`}>
        <button
          data-testid={`sort-${col}`}
          onClick={() => toggleSort(col)}
          className={`inline-flex items-center gap-1 ${justify} uppercase tracking-widest hover:text-black transition-colors ${active ? "text-black" : ""}`}
        >
          {label}
          {active ? (sort.dir === "asc" ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
        </button>
      </th>
    );
  };

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search).get("company");
    api.get("/companies").then((r) => {
      setCompanies(r.data);
      if (qs && r.data.some((c) => c.company_id === qs)) setCompanyId(qs);
      else if (r.data[0]) setCompanyId(r.data[0].company_id);
    });
  }, []);

  const tick = useRefreshTick();
  const loadReopenLog = (cid) => {
    api.get(`/payroll/reopen-log?company_id=${cid}`).then((r) => setReopenEvents(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!companyId) return;
    api.get(`/payroll/runs?company_id=${companyId}`).then((r) => setRuns(r.data));
    loadReopenLog(companyId);
  }, [companyId, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  const reopenRun = async (r) => {
    const warn = `Reopen payroll run ${r.run_number || r.pay_date}?\n\nThis opens the run in Run Payroll as an editable draft with all figures carried over. The original run STAYS in history until you re-finalize — finalizing then replaces it. If you abandon the edits (Clear draft), the original is left untouched.\n\nContinue?`;
    if (!window.confirm(warn)) return;
    try {
      const { data } = await api.post(`/payroll/runs/${r.run_id}/reopen`);
      const d = data.draft;
      const payoutsObj = {};
      (d.vacation_payouts || []).forEach((id) => { payoutsObj[id] = true; });
      await api.put("/payroll/draft", { company_id: companyId, draft: {
        hours: d.hours || {},
        payMethods: d.payment_methods || {},
        payouts: payoutsObj,
        payoutAmounts: d.vacation_payout_amounts || {},
        payoutReasons: d.vacation_payout_reasons || {},
        applyHoliday: d.apply_holiday_pay ?? true,
        holidayOverrides: d.holiday_overrides || {},
        holidayChoice: d.holiday_pay_choice || {},
        vacOverrides: d.vacation_accrual_overrides || {},
        vacReasons: d.vacation_override_reasons || {},
        lineOverrides: d.line_overrides || {},
        adjustments: d.adjustments || {},
        excluded: d.excluded_employee_ids || [],
        supersedesRunId: d.supersedes_run_id || r.run_id,
        scheduleName: d.schedule_name || "",
        meta: {
          pay_period_start: d.pay_period_start, pay_period_end: d.pay_period_end,
          pay_date: d.pay_date, pay_frequency: d.pay_frequency,
        },
        savedAt: Date.now(),
      }, reopened: r.run_number || r.pay_date });
      toast.success("Run opened for editing — finalize to replace it");
      navigate(`${base}/run-payroll?company=${companyId}`);
    } catch (e) {
      toast.error("Could not reopen run");
    }
  };

  return (
    <div className="w-full">
      <CompanyBreadcrumb label="Payroll History" />
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <h1 className="text-4xl font-black tracking-tight">Payroll History</h1>
          <p className="text-sm text-muted-foreground mt-1 font-mono uppercase tracking-widest">{yearRuns.length} runs in {year}{runs.length !== yearRuns.length ? ` · ${runs.length} total` : ""}</p>
        </div>
        {years.length > 0 && (
          <div className="flex border border-black" data-testid="history-year-tabs">
            {years.map((y) => (
              <button key={y} data-testid={`history-year-${y}`} onClick={() => setYear(y)} className={`px-4 py-2.5 text-xs font-mono uppercase tracking-widest border-l first:border-l-0 border-black transition-colors ${year === y ? "bg-black text-white" : "bg-white hover:bg-neutral-100"}`}>
                {y}{y === String(new Date().getFullYear()) ? " · current" : ""}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="border border-border bg-white">
        {yearRuns.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                  <Th col="run_number" label="Run #" />
                  <Th col="pay_date" label="Pay Date" />
                  <Th col="period" label="Period" />
                  <Th col="frequency" label="Frequency" />
                  <Th col="emp" label="Emp" align="center" />
                  <Th col="gross" label="Gross" align="right" />
                  <Th col="ded" label="Deductions" align="right" />
                  <Th col="net" label="Net" align="right" />
                  <th className="px-5 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <tr key={r.run_id} data-testid={`run-row-${r.run_id}`} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="px-5 py-3 font-mono text-xs">{r.run_number || "—"}{r.off_cycle && <span data-testid={`history-off-cycle-${r.run_id}`} className="ml-2 text-[9px] font-mono uppercase tracking-widest border border-amber-500 text-amber-700 px-1 py-0.5">off-cycle</span>}</td>
                    <td className="px-5 py-3 font-mono">{r.pay_date}</td>
                    <td className="px-5 py-3 font-mono text-xs text-muted-foreground">{r.pay_period_start} → {r.pay_period_end}</td>
                    <td className="px-5 py-3">{FREQ_LABELS[r.pay_frequency]}</td>
                    <td className="px-5 py-3 text-center tabular-nums">{r.employee_count}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(r.totals?.gross)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-red-600">{money(r.totals?.total_deductions)}</td>
                    <td className="px-5 py-3 text-right tabular-nums text-emerald-600 font-medium">{money(r.totals?.net_pay)}</td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`${base}/history/${r.run_id}`} data-testid={`view-run-${r.run_id}`} className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest hover:text-black text-muted-foreground">
                        Open <ArrowUpRight className="h-3 w-3" />
                      </Link>
                      {isAdmin && (
                        <button data-testid={`reopen-run-${r.run_id}`} onClick={() => reopenRun(r)} className="ml-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-amber-600 transition-colors">
                          <RotateCcw className="h-3.5 w-3.5" /> Reopen
                        </button>
                      )}
                      {isAdmin && (
                        <button data-testid={`delete-run-${r.run_id}`} onClick={async () => {
                          if (!window.confirm(`Delete payroll run ${r.run_number || r.pay_date}? This reverses its vacation-bank movements and cannot be undone.`)) return;
                          try { await api.delete(`/payroll/runs/${r.run_id}`); setRuns((rs) => rs.filter((x) => x.run_id !== r.run_id)); toast.success("Run deleted"); }
                          catch (e) { toast.error("Could not delete run"); }
                        }} className="ml-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-red-600 transition-colors">
                          <Trash2 className="h-3.5 w-3.5" /> Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="px-5 py-20 text-center">
            <History className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground">{runs.length ? `No payroll runs with a ${year} pay date.` : "No payroll runs recorded for this company yet."}</p>
          </div>
        )}
      </div>

      {reopenEvents.length > 0 && (
        <div className="mt-8 border border-border bg-white" data-testid="reopen-trail">
          <div className="flex items-center gap-2 border-b border-border px-5 py-3">
            <RotateCcw className="h-3.5 w-3.5 text-amber-600" />
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground">Reopen history</h2>
          </div>
          <ul className="divide-y divide-border">
            {reopenEvents.map((ev) => (
              <li key={ev.event_id} data-testid={`reopen-event-${ev.event_id}`} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                <span>
                  Run <span className="font-mono text-xs">{ev.run_number || ev.pay_date}</span>
                  <span className="text-muted-foreground"> · pay date {ev.pay_date}</span>
                  {" "}reopened by <span className="font-medium">{ev.reopened_by || "—"}</span>
                </span>
                <span className="font-mono text-xs text-muted-foreground">{fmtDateTime(ev.reopened_at)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
